import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Icon } from '@reservamos/elements';
import petBadge from 'images/badges/pet.svg';
import adjacentBadge from 'images/badges/adjacent-seat.svg';
import 'styles/components/SeatsLegend';
import { isSeatOccupied } from 'utils/purchase/seats';

/**
 * Check if the layout has a seat type
 * @param {Array} layout
 * @param {String|Function} check
 * @returns
 */
function hasSeatType(layout, check) {
  if (!layout) return false;

  return layout.some((row) => {
    return row.some((seats) => {
      return seats.some((seat) => {
        return typeof check === 'function' ? check(seat) : seat.category === check;
      });
    });
  });
}

/**
 * SeatsLegend component.
 *
 * @param {Object} props - The component props.
 * @param {Array} props.layout - The layout of the seats.
 * @param {boolean} props.showWomanLegends - Whether to show woman legends.
 * @param {boolean} props.showPetFriendly - Whether to show pet-friendly legends.
 * @param {number} props.occupied - The number of occupied seats.
 * @param {number} props.available - The number of available seats.
 * @param {number} props.selected - The number of selected seats.
 * @param {boolean} props.showAdjacentSeatLegend - Whether to show adjacent seat legend.
 * @param {boolean} props.adjacentToggleActive - Whether the adjacent seat toggle is active.
 */
function SeatsLegend({
  layout,
  showWomanLegends,
  showPetFriendly,
  occupied,
  available,
  selected,
  showAdjacentSeatLegend,
  adjacentToggleActive,
}) {
  const { t } = useTranslation('seats');

  const hasHandicapSeat = hasSeatType(
    layout,
    (seat) => seat.category === 'seat_handicap' && !isSeatOccupied(seat),
  );
  const hasDisabledSeats = hasSeatType(layout, 'seat_disabled') || adjacentToggleActive;

  const hasAdjacentSeats = hasSeatType(
    layout,
    (seat) => seat.adjacentSeats && !isSeatOccupied(seat),
  );

  return (
    <div className="seats-legend-container">
      <div className="seats-legend-item">
        <i className="seat-available" />
        <p>{`${available} ${t('seat', { context: 'available', count: available })}`}</p>
      </div>

      <div className="seats-legend-item">
        <i className="seat-selected" />
        <p>{`${selected} ${t('seat', { context: 'selected', count: selected })}`}</p>
      </div>

      <div className="seats-legend-item">
        <i className="seat-occupied" />
        <p>{`${occupied} ${t('seat', { context: 'occupied', count: occupied })}`}</p>
      </div>

      {hasDisabledSeats && (
        <div className="seats-legend-item">
          <i className="seat-disabled" />
          <p>{t('seat', { context: 'disabled' })}</p>
        </div>
      )}

      {showPetFriendly && (
        <div className="seats-legend-item">
          <Icon type={petBadge} />
          <p>{t('passengers:type.pet_friendly')}</p>
        </div>
      )}

      {showAdjacentSeatLegend && hasAdjacentSeats && (
        <div className="seats-legend-item">
          <Icon type={adjacentBadge} />
          <p>{t('adjacent_seat.label')}</p>
        </div>
      )}

      {hasHandicapSeat && (
        <div className="seats-legend-item">
          <i className="seat-handicap-available" />
          <p>{t('seat', { context: 'handicap' })}</p>
        </div>
      )}

      {showWomanLegends && (
        <>
          <div className="seats-legend-item">
            <i className="woman-seat" />
            <p>{t('seat', { context: 'woman' })}</p>
          </div>
          <div className="seats-legend-item">
            <i className="woman-seat-selected" />
            <p>{t('seat', { context: 'selected_woman' })}</p>
          </div>
        </>
      )}
    </div>
  );
}

SeatsLegend.propTypes = {
  layout: PropTypes.array,
  showWomanLegends: PropTypes.bool,
  showPetFriendly: PropTypes.bool,
  occupied: PropTypes.number,
  available: PropTypes.number,
  selected: PropTypes.number,
  showAdjacentSeatLegend: PropTypes.bool,
  adjacentToggleActive: PropTypes.bool,
};

SeatsLegend.defaultProps = {
  layout: [],
  showWomanLegends: false,
  showPetFriendly: false,
  occupied: 0,
  available: 0,
  selected: 0,
  showAdjacentSeatLegend: false,
  adjacentToggleActive: false,
};

export default SeatsLegend;
